import { useMemo, useState } from 'react';
import { exportDocument, photoExportRows, type Manifest } from '@intentionalist/shared';
import {
  catalogCsv,
  catalogJson,
  humanBytes,
  photoBytes,
  photoExport,
  saveFile,
} from './exporter.js';
import type { Replica } from './replica.js';

/**
 * Export (Epic 11).
 *
 * The screen that makes NFR-FLEX-4's claim true: the manifest can leave without
 * anyone's permission. Every file is produced on the device, so there is no
 * request to wait for and no operator to ask (FR-11.4).
 *
 * Photos are the one thing that cannot be produced here, and the screen says so
 * rather than pretending otherwise -- it hands over signed links and a script,
 * and states the size first, because a 25 GB download is not something to start
 * by accident.
 */
export function Export({
  manifest,
  version,
  replica,
}: {
  manifest: Manifest;
  version: number;
  replica: Replica;
}) {
  const [includeHistory, setIncludeHistory] = useState(false);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const [fault, setFault] = useState<string | null>(null);

  const document = useMemo(() => exportDocument(manifest), [manifest, version]);
  const photos = useMemo(() => photoExportRows(manifest), [manifest, version]);
  const bytes = useMemo(() => photoBytes(manifest), [manifest, version]);

  const items = includeDeleted ? document.items : document.items.filter((item) => !item.deleted);

  const run = async (what: string, work: () => Promise<void> | void) => {
    setBusy(what);
    setFault(null);
    setSaid(null);
    try {
      await work();
    } catch {
      // Nothing here is retried automatically: an export is a deliberate act and
      // a silent second attempt would produce two files for one request.
      setFault('That export could not be produced. Nothing was saved.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="review">
      <h2>Export</h2>
      <p className="muted hint">
        Everything is produced on this device. No request is sent and nothing is kept.
      </p>

      <dl className="sync-state">
        <dt>Items</dt>
        <dd>
          {document.counts.items}
          {document.counts.items !== items.length && ` · ${items.length} in the CSV`}
        </dd>

        <dt>Photos</dt>
        <dd>
          {photos.length === 0 ? (
            <span className="muted">none uploaded</span>
          ) : (
            `${photos.length} · about ${humanBytes(bytes)}`
          )}
        </dd>
      </dl>

      <div className="field">
        <label>
          <input
            type="checkbox"
            checked={includeHistory}
            onChange={(e) => setIncludeHistory(e.target.checked)}
          />{' '}
          Include the full change history in the JSON
        </label>
        <p className="muted hint">
          Much larger, and the only version that can answer what you owned on a past date after it
          leaves here.
        </p>
      </div>

      <div className="field">
        <label>
          <input
            type="checkbox"
            checked={includeDeleted}
            onChange={(e) => setIncludeDeleted(e.target.checked)}
          />{' '}
          Include deleted items in the CSV
        </label>
        <p className="muted hint">
          The JSON always includes them, flagged — a deleted item is recoverable for 30 days.
        </p>
      </div>

      <div className="actions">
        <button
          type="button"
          className="primary"
          disabled={busy !== null}
          onClick={() =>
            void run('json', async () => {
              const events = includeHistory ? await replica.log() : undefined;
              const file = catalogJson(manifest, {
                ...(events === undefined ? {} : { events }),
              });
              saveFile(file);
              setSaid(`Saved ${file.name}.`);
            })
          }
        >
          {busy === 'json' ? 'Preparing…' : 'Save JSON'}
        </button>

        <button
          type="button"
          className="primary"
          disabled={busy !== null}
          onClick={() =>
            void run('csv', () => {
              const file = catalogCsv(items);
              saveFile(file);
              setSaid(`Saved ${file.name}.`);
            })
          }
        >
          {busy === 'csv' ? 'Preparing…' : 'Save CSV'}
        </button>

        <button
          type="button"
          className="primary"
          disabled={busy !== null || photos.length === 0}
          onClick={() =>
            void run('photos', async () => {
              const result = await photoExport(manifest);
              for (const file of result.files) saveFile(file);

              setSaid(
                result.unsigned.length === 0
                  ? `Saved links and a download script for ${result.requested} photos.`
                  : `Saved links for ${result.requested - result.unsigned.length} of ${result.requested} photos. ${result.unsigned.length} could not be signed and are listed in neither file.`,
              );
            })
          }
        >
          {busy === 'photos' ? 'Asking for links…' : 'Save photo links'}
        </button>
      </div>

      {photos.length > 0 && (
        <p className="muted hint">
          The photo links expire within the hour and the bytes travel at your connection&apos;s
          speed, so the download runs on your machine rather than here. Run the saved script in an
          empty folder; re-export if the links expire part way.
        </p>
      )}

      {said !== null && (
        <p role="status" aria-live="polite">
          {said}
        </p>
      )}
      {fault !== null && (
        <p className="warn" role="alert">
          {fault}
        </p>
      )}
    </div>
  );
}
